/**
 * Cookie Consent Banner
 * GDPR/PECR compliant cookie consent for UK websites
 */
import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Button } from '../ui/button';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay, then trigger entrance animation on next frame
      const t = setTimeout(() => {
        setIsVisible(true);
        requestAnimationFrame(() => setHasMounted(true));
      }, 1000);
      return () => clearTimeout(t);
    }
  }, []);

  const handleAcceptAll = () => {
    const allConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allConsent));
    setIsVisible(false);
    
    // Initialize analytics if accepted
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted',
      });
    }
  };

  const handleRejectAll = () => {
    const minimalConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(minimalConsent));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const customConsent = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(customConsent));
    setIsVisible(false);
    
    // Update analytics consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': preferences.analytics ? 'granted' : 'denied',
        'ad_storage': preferences.marketing ? 'granted' : 'denied',
      });
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay (visual dim only — pointer-events-none so it never blocks form fields/buttons beneath it) */}
      <div
        className="fixed inset-0 bg-black/40 z-[9998] pointer-events-none"
        aria-hidden="true"
      />
      
      {/* Cookie Banner — outer wrapper is pointer-events-none so its
          full-width padding gutters never absorb clicks meant for the
          page below it. The inner card re-enables pointer events. */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-[9999] p-3 sm:p-6 pointer-events-none transition-all duration-300 ease-out ${
          hasMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'
        }`}
      >
        <div className="pointer-events-auto max-w-4xl mx-auto bg-[#1a1a1a] rounded-2xl border border-white/10 shadow-2xl overflow-hidden max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-3rem)] flex flex-col">
          {/* Premier champagne hairline */}
          <div className="h-[2px] bg-gradient-to-r from-transparent via-[#c9a96e] to-transparent flex-shrink-0" aria-hidden="true" />
          {!showSettings ? (
            /* Main Banner */
            <div className="p-4 sm:p-6 overflow-y-auto">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="hidden sm:flex w-12 h-12 bg-[#00AEEF]/20 rounded-xl items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-[#00AEEF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Cookie className="w-5 h-5 text-[#00AEEF] sm:hidden flex-shrink-0" />
                    <h3 className="text-base sm:text-xl font-semibold text-white">We Value Your Privacy</h3>
                  </div>
                  <p className="text-gray-400 text-xs sm:text-sm leading-relaxed mb-4">
                    We use cookies to enhance your browsing experience, serve personalised content, and analyse our traffic. 
                    By clicking "Accept All", you consent to our use of cookies. You can manage your preferences or reject 
                    non-essential cookies.
                  </p>
                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3">
                    <Button
                      onClick={handleAcceptAll}
                      className="bg-[#00AEEF] hover:bg-[#00AEEF]/90 text-white w-full sm:w-auto justify-center"
                      data-testid="cookie-accept-all"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Accept All
                    </Button>
                    <Button
                      onClick={handleRejectAll}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10 w-full sm:w-auto justify-center"
                      data-testid="cookie-reject-all"
                    >
                      Reject All
                    </Button>
                    <Button
                      onClick={() => setShowSettings(true)}
                      variant="ghost"
                      className="text-gray-400 hover:text-white hover:bg-white/10 w-full sm:w-auto justify-center"
                      data-testid="cookie-manage-prefs"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Manage Preferences
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Settings Panel */
            <div className="p-4 sm:p-6 overflow-y-auto">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-base sm:text-xl font-semibold text-white">Cookie Preferences</h3>
                <button 
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-white p-1"
                  aria-label="Close cookie preferences"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                {/* Necessary Cookies */}
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium">Necessary Cookies</span>
                    <span className="text-xs text-green-400 bg-green-400/20 px-2 py-1 rounded">Always Active</span>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Essential for the website to function properly. Cannot be disabled.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium">Analytics Cookies</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00AEEF]"></div>
                    </label>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Help us understand how visitors interact with our website (Google Analytics).
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium">Marketing Cookies</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences(prev => ({ ...prev, marketing: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00AEEF]"></div>
                    </label>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Used to deliver personalised advertisements and measure their effectiveness.
                  </p>
                </div>

                {/* Functional Cookies */}
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium">Functional Cookies</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={(e) => setPreferences(prev => ({ ...prev, functional: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00AEEF]"></div>
                    </label>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Enable enhanced functionality like language preferences and live chat.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button
                  onClick={handleSavePreferences}
                  className="bg-[#00AEEF] hover:bg-[#00AEEF]/90 text-white w-full sm:w-auto justify-center"
                  data-testid="cookie-save-prefs"
                >
                  Save Preferences
                </Button>
                <Button
                  onClick={() => setShowSettings(false)}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 w-full sm:w-auto justify-center"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
